import { gettext } from '@eventbrite/i18n';
import type { CompactCalendarCellProps } from '@eventbrite/timed-entry';
import type { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
import {
    DateEventStatus,
    type CompactCalendarData,
    type EventDate,
    type ScrollCalendarData,
} from '../../../state/infiniteCalendarSeries';
import defaultStyles from './CompactCalendar.module.scss';
import rebrandingStyles from './CompactCalendar.rebranding.module.scss';

import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { getLocalMidnightDate } from '../../InfiniteCalendarSeries/utils';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandingStyles,
};

export function _getDateCellKey(
    eventDate: EventDate,
    today: Moment,
    timezone: string,
): DateEventStatus {
    const eventDay = momentTimezone.tz(eventDate.date, timezone).startOf('day');

    if (eventDay.isBefore(today)) {
        return DateEventStatus.unavailable;
    }
    switch (true) {
        case eventDate.checkoutEnabled && eventDate.highDemand:
            return DateEventStatus.highDemand;
        case eventDate.checkoutEnabled:
            return DateEventStatus.available;
        case eventDate.soldOut:
            return DateEventStatus.soldOut;
        default:
            // in this case tickets are not available for sale, but the date have slots with tickets, therefore it will be shown as available
            return DateEventStatus.available;
    }
}

export function mapEventDatesToScrollCalendarData({
    eventDates,
    previousScrollCalendarData,
    month,
    year,
    timezone,
}: {
    eventDates: EventDate[];
    previousScrollCalendarData: ScrollCalendarData;
    month: number;
    year: number;
    timezone: string;
}) {
    const today = momentTimezone.tz(new Date(), timezone).startOf('day');
    const calendarData: ScrollCalendarData = previousScrollCalendarData || {
        years: {},
    };
    calendarData.years ||= {};

    for (const eventDate of eventDates) {
        const date = getLocalMidnightDate(eventDate.date);
        const year = date.getFullYear();
        const month = date.getMonth();
        const yearData = calendarData.years[year] || { months: {} };
        const monthData = yearData.months[month] || {
            dates: {},
        };

        monthData.dates[date.getDate()] ||= {
            dateCellKey: _getDateCellKey(eventDate, today, timezone),
            dateString: eventDate.date,
            sessions: [],
        };
    }

    calendarData.years[year].months[month].isLoading = false;

    return calendarData;
}

export const _getEventDatesByMonth = (eventData: EventDate[], locale: string) =>
    eventData.reduce((acc, event) => {
        const month = new Date(event.date).toLocaleString(locale, {
            month: 'long',
        });
        acc[month] = [...(acc[month] || []), event];
        return acc;
    }, {} as Record<string, EventDate[]>);

export const _getCellClassName = (
    event: EventDate,
    isAppRebrandActive: boolean,
) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    const classNames = [];
    if (isAppRebrandActive) {
        classNames.push(styles.dateCell);
    }
    if (event.highDemand) {
        classNames.push(styles.highDemandDateCell);
    }
    if (!event.checkoutEnabled) {
        classNames.push(styles.disabledDateCell);
    }
    if (event.soldOut) {
        classNames.push(styles.soldOutDateCell);
    }
    return classNames.join(' ');
};

export function _getCellProps(event: EventDate, isAppRebrandActive: boolean) {
    const cellProps: CompactCalendarCellProps['cellProps'] = {
        isDisabled: false,
        icons: [],
    };
    const className = _getCellClassName(event, isAppRebrandActive);
    if (event.soldOut) {
        cellProps.isDisabled = true;
        cellProps.disabledText = gettext('Sold out').toString();
    }
    cellProps.className = className;
    return cellProps;
}

export function mapEventDatesToCompactCalendarData(
    eventData: EventDate[],
    timezone: string,
    locale: string,
    isAppRebrandActive: boolean,
) {
    const calendarData: CompactCalendarData = [];
    const eventDatesByMonth = _getEventDatesByMonth(eventData, locale);
    for (const monthName in eventDatesByMonth) {
        const events = eventDatesByMonth[monthName];
        const dateCells: CompactCalendarCellProps[] = events.map((event) => ({
            cellProps: _getCellProps(event, isAppRebrandActive),
            date: event.date,
            id: event.sessionId as string, // as string - we will get sessionId for the compact calendar
            sessionTime: event.sessionStartDatetime,
            timezone,
        }));
        calendarData.push({
            monthLabel: monthName,
            dateCells,
        });
    }

    return calendarData;
}

export function trimISODatetimeToDate(dateString: string): string {
    return dateString.slice(0, 10);
}
